import { jsxs, jsx } from 'react/jsx-runtime';
import { Input } from '../../atoms/Input/Input.js';
import { useState, useRef, useEffect } from 'react';
import { Option } from './Option.js';
import Search from '../../../icons/Search.js';
import { LoadingSpinner } from '../../atoms/LoadingSpinner/LoadingSpinner.js';
import { ChevronDown } from '../../../icons/ChevronDown.js';
import { SubBuildingOptions } from './SubBuildingOptions.js';

const styles = {
    dropdown: 'absolute z-10 w-full max-h-60 overflow-y-auto border border-neutral-0 bg-neutral-0 shadow-[0_6px_16px_rgba(0,0,0,0.12)] rounded-radius-100 mt-1',
    errorMessage: 'text-body-small text-text-error mt-1',
};
const errorMessage = 'Please enter at least 7 characters to search for an address.';
/**
 * AddressSearchInput component for searching and selecting addresses.
 *
 * Provides an autocomplete search input that allows users to search for addresses with a minimum
 * of 7 characters. Supports selection of addresses with optional sub-building options.
 *
 * @component
 * @example
 * ```tsx
 * <AddressSearchInput
 *   label="Address"
 *   placeholder="Enter your address"
 *   onAddressSearch={(value) => searchAddresses(value)}
 *   onAddressSearchSelect={(address) => handleAddressSelection(address)}
 *   addressSearchResult={searchResults}
 *   minSearchLength={7}
 * />
 * ```
 *
 * @param {AddressSearchInputProps} props - The component props
 * @param {Function} props.onAddressSearch - Callback function triggered when user searches for an address
 * @param {Function} props.onAddressSearchSelect - Callback function triggered when user selects an address
 * @param {AddressSearchResultWithSubBuildings | AddressSearchResultWithSubBuildings[]} props.addressSearchResult - The search results to display
 * @param {string} [props.placeholder] - Placeholder text for the input field
 * @param {string} [props.label] - Label text for the input field
 * @param {number} [props.minSearchLength=7] - Minimum number of characters required to trigger search
 *
 * @returns {React.ReactElement} The rendered address search input component
 */
const AddressSearchInput = (props) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState(undefined);
    const [hasError, setHasError] = useState(false);
    const [addressSearchResultsList, setAddressSearchResults] = useState(undefined);
    const [displayDropdown, setDisplayDropdown] = useState(false);
    const [expandedAddressId, setExpandedAddressId] = useState(null);
    const [searching, setSearching] = useState(false);
    const dropdownRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const { onAddressSearch, onAddressSearchSelect, addressSearchResult, placeholder, label, minSearchLength = 7, icon, isSuccess, isError, } = props;
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (inputValue === '') {
            setDisplayDropdown(false);
            setSelectedOption(undefined);
            setHasError(false);
            setSearching(false);
            return;
        }
        debounceTimerRef.current = setTimeout(() => {
            if (inputValue.length >= minSearchLength && !selectedOption) {
                onAddressSearch?.(inputValue);
                setSearching(true);
            }
            if (inputValue.length < minSearchLength) {
                setHasError(true);
                setDisplayDropdown(false);
                setSearching(false);
                return;
            }
            setHasError(false);
        }, 300);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [inputValue, selectedOption, onAddressSearch]);
    // Handle click event outside and esc key to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDisplayDropdown(false);
            }
        };
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setDisplayDropdown(false);
            }
        };
        if (displayDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }
    }, [displayDropdown]);
    useEffect(() => {
        if (addressSearchResult) {
            setAddressSearchResults(addressSearchResult);
            setSearching(false);
            setDisplayDropdown(true);
        }
    }, [addressSearchResult]);
    const selectAddress = (address) => {
        setSelectedOption(address);
        if (address.subBuilding &&
            Array.isArray(address.subBuilding) &&
            address.subBuilding.length > 0) {
            setExpandedAddressId(address.id);
            return;
        }
        if (onAddressSearchSelect) {
            onAddressSearchSelect(address);
            setInputValue(address.address);
            setDisplayDropdown(false);
            setExpandedAddressId(null);
        }
    };
    const selectSubBuilding = (subBuildingAddress) => {
        if (onAddressSearchSelect) {
            onAddressSearchSelect(subBuildingAddress);
            setInputValue(subBuildingAddress.address);
            setDisplayDropdown(false);
            setExpandedAddressId(null);
            setSelectedOption(subBuildingAddress);
        }
    };
    const handleInputChange = (value) => {
        setInputValue(value);
        if (selectedOption && value !== selectedOption.address) {
            setSelectedOption(undefined);
            setExpandedAddressId(null);
        }
    };
    const handleInputFocus = () => {
        if (addressSearchResult && addressSearchResultsList)
            setDisplayDropdown(true);
    };
    const handleToggleAndSelect = (address) => expandedAddressId === address.id ? setExpandedAddressId(null) : selectAddress(address);
    const dropdown = () => {
        if (displayDropdown) {
            if (addressSearchResultsList?.length) {
                return addressSearchResultsList.map((address) => {
                    return (jsxs("div", { children: [jsx(Option, { address: address, selectedOption: selectedOption, selectAddress: () => handleToggleAndSelect(address), children: address.subBuilding && address.totalUnits ? (jsx(ChevronDown, { isOpen: expandedAddressId === address.id })) : null }), jsx(SubBuildingOptions, { isExpanded: expandedAddressId === address.id, selectSubBuilding: selectSubBuilding, address: address })] }, address.id));
                });
            }
            else if (addressSearchResultsList && !addressSearchResultsList?.length) {
                return jsx("div", { className: "p-space-400 py-space-200 text-body", children: "No results found" });
            }
        }
        else
            return;
    };
    const iconElement = () => {
        if (isSuccess)
            return jsx(CircleCheck, {});
        if (searching)
            return jsx(LoadingSpinner, {});
        if (!searching)
            return icon ?? jsx(Search, {});
    };
    return (jsxs("div", { className: "flex flex-col w-full", children: [jsx(Input, { label: label, placeholder: placeholder, value: inputValue, onChange: (e) => handleInputChange(e.target.value), onFocus: handleInputFocus, rightIcon: iconElement(), hasError: isError }), hasError && jsx("p", { className: styles['errorMessage'], children: errorMessage }), jsx("div", { ref: dropdownRef, className: "relative w-full", role: "listbox", "aria-label": "Address results", children: jsx("div", { className: styles['dropdown'], children: dropdown() }) })] }));
};
const CircleCheck = () => {
    return (jsx("svg", { width: "17", height: "16", viewBox: "0 0 17 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M8.375 1C5.84375 1 3.5625 2.34375 2.3125 4.5C1.03125 6.6875 1.03125 9.34375 2.3125 11.5C3.5625 13.6875 5.84375 15 8.375 15C10.875 15 13.1562 13.6875 14.4062 11.5C15.6875 9.34375 15.6875 6.6875 14.4062 4.5C13.1562 2.34375 10.875 1 8.375 1ZM8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM11.7188 6.375L7.71875 10.375C7.53125 10.5625 7.1875 10.5625 7 10.375L5 8.375C4.8125 8.1875 4.8125 7.84375 5 7.65625C5.1875 7.46875 5.53125 7.46875 5.71875 7.65625L7.375 9.3125L11 5.65625C11.1875 5.46875 11.5312 5.46875 11.7188 5.65625C11.9062 5.84375 11.9062 6.1875 11.7188 6.375Z", fill: "#3B8524" }) }));
};

export { AddressSearchInput, CircleCheck };
